/**
 * Contracts for /api/search/facets: the per-category search filter options
 * (utils/search-facets.ts). The request takes the same params as
 * /api/search/listings; the text query is ignored.
 */
import { z } from 'zod';

export const FacetOptionSchema = z.object({
  /** What to send back as the filter value: `brand=Louis Vuitton`. */
  value: z.string(),
  label: z.string(),
  count: z.number().int().nonnegative(),
  /** Image URL for the option (brands, materials), when there is one. */
  icon: z.string().nullable(),
});
export type FacetOption = z.infer<typeof FacetOptionSchema>;

/** One drawer section, in the order the page shows them (utils/category-facet-layout.ts). */
export const FacetSectionOptionsSchema = z.object({
  /** An attribute key (`brand`), `type`, or a tag section's key (`species`): where the choice goes. */
  key: z.string(),
  title: z.string(),
  kind: z.enum(['type', 'attribute', 'tags']),
  options: z.array(FacetOptionSchema),
});
export type FacetSectionOptions = z.infer<typeof FacetSectionOptionsSchema>;

export const SearchFacetsSchema = z.object({
  /** Listings matching every filter given. */
  total: z.number().int().nonnegative(),
  gender: z.array(FacetOptionSchema),
  ageGroup: z.array(FacetOptionSchema),
  size: z.array(FacetOptionSchema),
  color: z.array(FacetOptionSchema),
  brand: z.array(FacetOptionSchema),
  material: z.array(FacetOptionSchema),
  /**
   * Every section of the page's category group in order, attribute ones
   * included. Absent off a category (home, unscoped search), where the
   * attribute arrays are the drawer. Optional so a client on the previous
   * deploy still parses.
   */
  sections: z.array(FacetSectionOptionsSchema).optional(),
});
export type SearchFacets = z.infer<typeof SearchFacetsSchema>;

export const SearchFacetsResponseSchema = SearchFacetsSchema.extend({ success: z.literal(true) });
export type SearchFacetsResponse = z.infer<typeof SearchFacetsResponseSchema>;

export const SearchFacetsErrorSchema = z.object({ success: z.literal(false), error: z.string() });
export type SearchFacetsError = z.infer<typeof SearchFacetsErrorSchema>;
